import { Fragment, useMemo, useState, useEffect } from "react"
import { useSortBy } from "react-instantsearch-hooks-web"
import { Button, Text, Menu, MenuItem, Portal, Box } from "@chakra-ui/react"
import { sortOptions, mapSortToReplica } from "../FilterTransforms" 
import { CheckIcon } from "../../../icons"
import { useWorkbenchStore } from "../../../store/store"

export default function SortByMenu({ baseIndexName, id = "sort-by", label = "Sort By" }) {
  const [isOpen, setIsOpen] = useState(false)
  const setSortedIndexName = useWorkbenchStore((s) => s.setSortedIndexName)

  const items = useMemo(() =>
    sortOptions.map(opt => ({
      label: opt.text,
      value: mapSortToReplica(baseIndexName, opt.value),
      key: opt.value,
    })), [baseIndexName])

  const { refine, options, currentRefinement } = useSortBy({ items })
  const isDefault = currentRefinement === mapSortToReplica(baseIndexName, "default")
  const active = !isDefault

  useEffect(() => {
    if (isDefault) {
      setSortedIndexName("")
    } else {
      setSortedIndexName(currentRefinement) 
    }
  }, [currentRefinement, isDefault, setSortedIndexName])

  useEffect(() => {
    let raf;
    const onPopstate = () => {
      // let IndexSearch update the sortBy state based on the URL params first
      raf = requestAnimationFrame(() => {
        const params = new URLSearchParams(window.location.search)
        const key = params.get("sort") || "default"
        const target = mapSortToReplica(baseIndexName, key)
        if (currentRefinement !== target) {
          refine(target)
        }
      })
    }
    window.addEventListener("popstate", onPopstate)
    return () => {
      window.removeEventListener("popstate", onPopstate)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [baseIndexName, currentRefinement, refine])
  
  return (
    <Fragment>
      <Menu.Root id={id} autoSelect={false} closeOnSelect={false} onOpenChange={(d)=>setIsOpen(d.open)} isLazy>
        <>
          <Menu.Trigger outline="none" asChild>
            <Button
              display="flex" alignItems="center" textTransform="uppercase"
              backgroundColor={!isOpen ? "white" : "var(--purple)"}
              border={active || isOpen ? "solid 2px var(--purple)" : "solid 2px var(--light-purple)"}
              height="40px" borderRadius="20px" padding="0px 20px" variant="plain"
              color={!isOpen ? "var(--purple)" : "white"}
              _hover={{ backgroundColor: "var(--purple)", color: "white", borderColor: "var(--purple)" }}
              _active={{ backgroundColor: "var(--purple)", color: "white", borderColor: "var(--purple)" }}
              _focus={{ boxShadow: "none" }}
              overflow="hidden" data-cy={`${id}-button`}
            >
              <Text fontSize=".8rem" letterSpacing="0.128em" fontFamily="TTCommonsMedium">{label}</Text>
            </Button>
          </Menu.Trigger>

          <Portal>
            <Menu.Positioner>
              <Menu.Content shadow="lg" py="4" px="3" overflowY="auto"
                minW={{ base: "13rem", smTo2xl: "15rem" }} maxH="25rem"
                borderRadius="20px" background="var(--light-purple)">
                <Menu.Item
                  fontWeight="semibold"
                  rounded="md"
                  onClick={() => refine(mapSortToReplica(baseIndexName, "default"))}
                  position="relative"
                  cursor="pointer"
                  color="gray.400"
                  minH="13px"
                  _hover={{ color: "var(--purple)" }}
                >
                  <Text position="absolute" top="0px" right="0px" fontSize="0.6875rem">
                    Reset
                  </Text>
                </Menu.Item>  

                {options.map((opt) => {
                  const isSelected = currentRefinement === opt.value
                  return (
                    <MenuItem
                      key={opt.value}
                      rounded="md"
                      h="36px"
                      onClick={() => refine(opt.value)}
                      cursor="pointer"
                      color="var(--purple)"                 
                      _hover={{ color: "var(--orange)" }}   
                      data-cy={`${id}-option-${opt.value}`}
                    >
                      <Box fontFamily="TTCOMMONSMEDIUM" fontSize="14px" lineHeight="1">
                        {opt.label}
                      </Box>
                      {isSelected && (
                        <Box marginLeft="auto">
                          <CheckIcon width="15px" height="15px" fillColor="var(--purple)" minWidth="15px" />
                        </Box>
                      )}
                    </MenuItem>
                  )
                })}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </>
      </Menu.Root>
    </Fragment>
  )
}
